// src/services/mediaFileService.js
//
// Shared frontend API wrapper for protected media files.
//
// PURPOSE
// -------
// Media file endpoints are protected by the same auth/session logic as the rest
// of hb_visits.
//
// Important:
// - Do not load protected media with raw <img src="/media/:id/file">
// - Raw <img> does not use apiClient
// - apiClient owns auth headers, browser/device/session headers and refresh flow
//
// Therefore we fetch the file as Blob through apiClient and render it as
// URL.createObjectURL(blob).

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  const apiMsg =
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail;

  return apiMsg || err?.message || "Request failed";
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);

  e.status = err?.response?.status;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[MediaFileService] ${context}`, {
      status: e.status,
      message,
      data: e.data,
    });
  }

  throw e;
}

export function getMediaFileUrl(media) {
  if (!media) return "";

  if (typeof media === "string") {
    return media;
  }

  return media.file_url || media.url || "";
}

export const fetchMediaBlob = async (mediaOrUrl) => {
  try {
    const rawUrl = getMediaFileUrl(mediaOrUrl);

    if (!rawUrl || `${rawUrl}`.trim() === "") {
      const e = new Error("fetchMediaBlob: file_url is required.");
      e.code = "MEDIA_URL_MISSING";
      throw e;
    }

    const cleanUrl = String(rawUrl).startsWith("/")
      ? String(rawUrl)
      : `/${rawUrl}`;

    const res = await apiClient.get(cleanUrl, {
      withCredentials: true,
      responseType: "blob",
    });

    return res?.data;
  } catch (err) {
    throwApiError("fetchMediaBlob", err);
  }
};

export const uploadOwnerMedia = async (ownerType, ownerId, files, options = {}) => {
  try {
    if (!ownerType || `${ownerType}`.trim() === "") {
      const e = new Error("uploadOwnerMedia: ownerType is required.");
      e.code = "MEDIA_OWNER_TYPE_MISSING";
      throw e;
    }

    if (ownerId === undefined || ownerId === null || `${ownerId}`.trim() === "") {
      const e = new Error("uploadOwnerMedia: ownerId is required.");
      e.code = "MEDIA_OWNER_ID_MISSING";
      throw e;
    }

    const fileList = Array.isArray(files) ? files : Array.from(files || []);

    if (!fileList.length) {
      const e = new Error("uploadOwnerMedia: at least one file is required.");
      e.code = "MEDIA_FILES_MISSING";
      throw e;
    }

    const formData = new FormData();

    fileList.forEach((file) => {
      formData.append("images", file);
    });

    if (options.media_role) {
      formData.append("media_role", options.media_role);
    }

    const res = await apiClient.post(
      `/media/${encodeURIComponent(ownerType)}/${encodeURIComponent(ownerId)}`,
      formData,
      {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("uploadOwnerMedia", err);
  }
};

export const setPrimaryOwnerMedia = async (
  ownerType,
  ownerId,
  mediaId,
  options = {}
) => {
  try {
    const res = await apiClient.patch(
      `/media/${encodeURIComponent(ownerType)}/${encodeURIComponent(ownerId)}/${encodeURIComponent(mediaId)}/primary`,
      options.media_role ? { media_role: options.media_role } : {},
      {
        withCredentials: true,
        headers: { "Content-Type": "application/json" },
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("setPrimaryOwnerMedia", err);
  }
};

export const deleteOwnerMedia = async (
  ownerType,
  ownerId,
  mediaId,
  options = {}
) => {
  try {
    const params = options.media_role ? { media_role: options.media_role } : {};

    const res = await apiClient.delete(
      `/media/${encodeURIComponent(ownerType)}/${encodeURIComponent(ownerId)}/${encodeURIComponent(mediaId)}`,
      {
        withCredentials: true,
        params,
      }
    );

    return res?.data;
  } catch (err) {
    throwApiError("deleteOwnerMedia", err);
  }
};

export default {
  getMediaFileUrl,
  fetchMediaBlob,
  uploadOwnerMedia,
  setPrimaryOwnerMedia,
  deleteOwnerMedia,
};